import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Library, 
  Plus, 
  Search, 
  Video, 
  FileText, 
  Headphones, 
  Presentation,
  Heart,
  Eye,
  ExternalLink,
  Filter,
  BookOpen,
  Dumbbell,
  Brain,
  Apple,
  TrendingUp,
  Target,
  Star,
  Download,
  Award,
  Activity,
  Trash2
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

const BOOK_URL = 'https://customer-assets.emergentagent.com/job_player-connect-ikons/artifacts/67uo9q3e_Libro_Marca_Personal_Deportiva_por_Edgar_Merino.pdf';

function ResourceLibrary() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'video',
    category: 'tactical',
    url: '',
    thumbnail_url: '',
    duration: '',
    author: ''
  });

  useEffect(() => {
    fetchResources();
    fetchFavorites();
    fetchStats();
  }, [selectedCategory, selectedType]);

  const handleBookDownload = () => {
    // Open the book immediately (synchronous) to avoid popup blockers
    // Track the download asynchronously in the background
    window.open(BOOK_URL, '_blank');
    
    // Track download in background (don't await)
    api.post('/book-downloads/track', {}).catch(() => {
      // Silently ignore tracking errors
    });
  };

  const fetchResources = async () => {
    try {
      let url = `/resources?`;
      if (selectedCategory !== 'all') url += `category=${selectedCategory}&`;
      if (selectedType !== 'all') url += `type=${selectedType}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}`;
      
      const response = await api.get(url);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Error al cargar recursos');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/resources/favorites/my');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/resources/stats/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = () => {
    fetchResources();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/resources', formData);
      toast.success('Recurso agregado');
      setDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        type: 'video',
        category: 'tactical',
        url: '',
        thumbnail_url: '',
        duration: '',
        author: ''
      });
      fetchResources();
      fetchStats();
    } catch (error) {
      console.error('Error creating resource:', error);
      toast.error('Error al crear recurso');
    }
  };

  const handleToggleFavorite = async (resourceId) => {
    try {
      const response = await api.post(`/resources/${resourceId}/favorite`, {});
      toast.success(response.data.message);
      fetchResources();
      fetchFavorites();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error al marcar favorito');
    }
  };

  const handleViewResource = async (resource) => {
    // Open the resource URL immediately
    window.open(resource.url, '_blank');
    
    // Track the view in the background (don't block opening)
    try {
      await api.get(`/resources/${resource.resource_id}`);
    } catch (error) {
      console.error('Error tracking resource view:', error);
    }
  };

  const handleDeleteResource = async (resourceId, resourceTitle) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${resourceTitle}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/resources/${resourceId}`);
      toast.success('Recurso eliminado');
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Error al eliminar recurso');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'article':
        return <FileText className="h-5 w-5" />;
      case 'pdf':
        return <BookOpen className="h-5 w-5" />;
      case 'webinar':
        return <Presentation className="h-5 w-5" />;
      case 'podcast':
        return <Headphones className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'ikons':
        return <Star className="h-4 w-4" />;
      case 'tactical':
        return <Target className="h-4 w-4" />;
      case 'technical':
        return <Dumbbell className="h-4 w-4" />;
      case 'mental':
        return <Brain className="h-4 w-4" />;
      case 'physical':
        return <Activity className="h-4 w-4" />;
      case 'nutrition':
        return <Apple className="h-4 w-4" />;
      case 'career':
        return <TrendingUp className="h-4 w-4" />;
      case 'goalkeeper':
        return <Target className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'ikons': 'bg-amber-100 text-amber-800 border-amber-300',
      'tactical': 'bg-blue-100 text-blue-800 border-blue-300',
      'technical': 'bg-purple-100 text-purple-800 border-purple-300',
      'mental': 'bg-green-100 text-green-800 border-green-300',
      'physical': 'bg-orange-100 text-orange-800 border-orange-300',
      'nutrition': 'bg-pink-100 text-pink-800 border-pink-300',
      'career': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'goalkeeper': 'bg-cyan-100 text-cyan-800 border-cyan-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'ikons': 'Ikons Exclusivo',
      'tactical': 'Táctica',
      'technical': 'Técnica',
      'mental': 'Mental',
      'physical': 'Físico',
      'nutrition': 'Nutrición',
      'career': 'Carrera',
      'goalkeeper': 'Porteros'
    };
    return labels[category] || category;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'video': 'Video',
      'article': 'Artículo',
      'pdf': 'PDF',
      'webinar': 'Webinar',
      'podcast': 'Podcast'
    };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="resource-library-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Library className="h-8 w-8 text-primary" />
              <h1 className="text-4xl font-bold tracking-tight">Biblioteca de Recursos</h1>
            </div>
            <p className="text-muted-foreground">Centro de conocimiento para tu desarrollo profesional</p>
          </div>
          
          {user?.role === 'agent' && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-resource-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Recurso
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Agregar Nuevo Recurso</DialogTitle>
                  <DialogDescription>Comparte contenido valioso con tus jugadores</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type">Tipo *</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value) => setFormData({ ...formData, type: value })}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="article">Artículo</SelectItem>
                          <SelectItem value="pdf">PDF</SelectItem>
                          <SelectItem value="webinar">Webinar</SelectItem>
                          <SelectItem value="podcast">Podcast</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="category">Categoría *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ikons">Ikons Exclusivo</SelectItem>
                          <SelectItem value="tactical">Táctica</SelectItem>
                          <SelectItem value="technical">Técnica</SelectItem>
                          <SelectItem value="mental">Mental</SelectItem>
                          <SelectItem value="physical">Físico</SelectItem>
                          <SelectItem value="nutrition">Nutrición</SelectItem>
                          <SelectItem value="career">Carrera</SelectItem>
                          <SelectItem value="goalkeeper">Porteros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Descripción *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="url">URL *</Label>
                    <Input
                      id="url"
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      required
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="thumbnail_url">URL de Miniatura</Label>
                      <Input
                        id="thumbnail_url"
                        type="url"
                        value={formData.thumbnail_url}
                        onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration">Duración</Label>
                      <Input
                        id="duration"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        placeholder="15 min"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="author">Autor/Creador</Label>
                    <Input
                      id="author"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="Nombre del autor"
                      className="mt-1"
                    />
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit">Agregar Recurso</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Featured Resource - Edgar Merino's Book */}
        <Card className="bento-card mb-6 overflow-hidden border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="featured-resource-section">
          <CardContent className="p-0">
            <div className="flex flex-col lg:flex-row">
              {/* Left Side - Visual */}
              <div className="lg:w-1/3 p-8 flex items-center justify-center bg-gradient-to-br from-amber-600 to-orange-700">
                <div className="text-center">
                  <div className="w-32 h-44 bg-white/20 backdrop-blur rounded-lg shadow-2xl mx-auto flex items-center justify-center relative border-2 border-white/30">
                    <BookOpen className="h-16 w-16 text-white" strokeWidth={1.5} />
                    <div className="absolute -top-3 -right-3 bg-yellow-400 rounded-full p-2 shadow-lg">
                      <Star className="h-5 w-5 text-yellow-800 fill-yellow-800" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <Award className="h-5 w-5 text-yellow-300" />
                    <span className="text-white font-semibold text-sm">Lectura Recomendada</span>
                  </div>
                </div>
              </div>
              
              {/* Right Side - Info */}
              <div className="lg:w-2/3 p-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-amber-200 text-amber-800 text-xs font-bold rounded-full uppercase tracking-wider">
                    Recurso Destacado
                  </span>
                  <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-semibold rounded-full">
                    PDF
                  </span>
                </div>
                
                <h2 className="text-3xl font-bold text-amber-900 mb-3">
                  Marca Personal Deportiva
                </h2>
                
                <p className="text-lg text-amber-700 mb-2">
                  <span className="font-semibold">Por Edgar Merino</span>
                </p>
                <p className="text-sm text-amber-600 mb-2">
                  Agente de Jugadores — Ikons Agency
                </p>
                
                <p className="text-amber-700 mb-6 leading-relaxed">
                  Una guía completa para futbolistas que desean construir y potenciar su marca personal. 
                  Aprende estrategias profesionales para destacar dentro y fuera del campo, 
                  gestionar tu imagen pública y maximizar tu carrera deportiva.
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <a 
                    href={BOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => api.post('/book-downloads/track', {}).catch(() => {})}
                    className="inline-flex items-center justify-center h-11 px-8 bg-amber-600 hover:bg-amber-700 text-white shadow-lg rounded-md font-medium transition-colors"
                    data-testid="featured-resource-download-btn"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Descargar Libro
                  </a>
                  <a 
                    href={BOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => api.post('/book-downloads/track', {}).catch(() => {})}
                    className="inline-flex items-center justify-center h-11 px-8 border-2 border-amber-600 text-amber-700 hover:bg-amber-100 rounded-md font-medium transition-colors"
                    data-testid="featured-resource-view-btn"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Ver en Navegador
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <Card className="bento-card mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Buscar recursos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    <SelectItem value="ikons">Ikons Exclusivo</SelectItem>
                    <SelectItem value="tactical">Táctica</SelectItem>
                    <SelectItem value="technical">Técnica</SelectItem>
                    <SelectItem value="mental">Mental</SelectItem>
                    <SelectItem value="physical">Físico</SelectItem>
                    <SelectItem value="nutrition">Nutrición</SelectItem>
                    <SelectItem value="career">Carrera</SelectItem>
                    <SelectItem value="goalkeeper">Porteros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="article">Artículo</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="podcast">Podcast</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="all" className="mb-6">
          <TabsList>
            <TabsTrigger value="all">
              Todos ({stats?.total || 0})
            </TabsTrigger>
            <TabsTrigger value="favorites">
              <Heart className="h-4 w-4 mr-2" />
              Favoritos ({favorites.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : resources.length === 0 ? (
              <Card className="bento-card">
                <CardContent className="p-12 text-center">
                  <Library className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No se encontraron recursos</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resources.map((resource) => (
                  <Card key={resource.resource_id} className="bento-card group overflow-hidden">
                    {resource.thumbnail_url && (
                      <div className="aspect-video w-full overflow-hidden bg-secondary relative">
                        <img
                          src={resource.thumbnail_url}
                          alt={resource.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 right-2">
                          <Button
                            size="icon"
                            variant="secondary"
                            className="rounded-full"
                            onClick={() => handleToggleFavorite(resource.resource_id)}
                          >
                            <Heart 
                              className={`h-4 w-4 ${resource.favorites?.includes(user?.user_id) ? 'fill-red-500 text-red-500' : ''}`}
                            />
                          </Button>
                        </div>
                      </div>
                    )}
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-2 rounded-lg ${getCategoryColor(resource.category)}`}>
                          {getCategoryIcon(resource.category)}
                        </div>
                        <Badge variant="outline" className={getCategoryColor(resource.category)}>
                          {getCategoryLabel(resource.category)}
                        </Badge>
                        <Badge variant="secondary" className="ml-auto">
                          {getTypeLabel(resource.type)}
                        </Badge>
                      </div>

                      <h3 className="text-lg font-semibold mb-2 line-clamp-2">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {resource.description}
                      </p>

                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                        {resource.author && <span>Por {resource.author}</span>}
                        {resource.duration && <span>{resource.duration}</span>}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {resource.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="h-3 w-3" />
                            {resource.favorites?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {user?.role === 'agent' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteResource(resource.resource_id, resource.title)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleViewResource(resource)}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Ver
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="favorites">
            {favorites.length === 0 ? (
              <Card className="bento-card">
                <CardContent className="p-12 text-center">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No tienes favoritos aún</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((resource) => (
                  <Card key={resource.resource_id} className="bento-card group overflow-hidden">
                    {resource.thumbnail_url && (
                      <div className="aspect-video w-full overflow-hidden bg-secondary relative">
                        <img
                          src={resource.thumbnail_url}
                          alt={resource.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className={getCategoryColor(resource.category)}>
                          {getCategoryLabel(resource.category)}
                        </Badge>
                        <Badge variant="secondary" className="ml-auto">
                          {getTypeLabel(resource.type)}
                        </Badge>
                      </div>

                      <h3 className="text-lg font-semibold mb-2 line-clamp-2">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {resource.description}
                      </p>

                      <div className="flex items-center gap-2">
                        {user?.role === 'agent' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteResource(resource.resource_id, resource.title)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleViewResource(resource)}
                          className="flex-1"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Ver Recurso
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default ResourceLibrary;
